import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { Animated, Pressable, StyleSheet, Text, View } from 'react-native';

import { ActionKind } from './new-action-modal';

interface Action {
	kind: NonNullable<ActionKind>;
	icon: keyof typeof Ionicons.glyphMap;
	label: string;
	color: string;
}

const ALL_ACTIONS: Action[] = [
	{ kind: 'account', icon: 'wallet-outline', label: 'New customer', color: '#007AFF' },
	{ kind: 'charge', icon: 'receipt-outline', label: 'Post charge', color: '#00897b' },
	{ kind: 'payment', icon: 'cash-outline', label: 'Record payment', color: '#43a047' },
	{ kind: 'credit', icon: 'remove-circle-outline', label: 'Issue credit', color: '#6a1b9a' },
	{ kind: 'refund', icon: 'return-up-back-outline', label: 'Issue refund', color: '#ef6c00' },
];

/**
 * A non-accounting action stacked above the accounting ones — e.g. the account page's
 * "New work order" / "New improvement request" (spec §3b).
 */
export interface FabExtraAction {
	key: string;
	icon: keyof typeof Ionicons.glyphMap;
	label: string;
	color: string;
	onPress: () => void;
}

/** One row of the stack; `key` also names its testIDs (`fab-action-<key>`, `fab-action-label-<key>`). */
type StackedAction = FabExtraAction;

export const AccountingFAB: React.FC<{
	onSelect: (k: ActionKind) => void;
	/** Filter actions. When on the account detail page, skip 'account' since
	 * we're already scoped to one. May exclude every accounting action. */
	exclude?: Array<NonNullable<ActionKind>>;
	/** Rendered above the accounting actions. */
	extraActions?: FabExtraAction[];
}> = ({ onSelect, exclude, extraActions }) => {
	const [open, setOpen] = useState(false);
	const [anim] = useState(new Animated.Value(0));
	const actions: StackedAction[] = [
		...(exclude ? ALL_ACTIONS.filter((a) => !exclude.includes(a.kind)) : ALL_ACTIONS).map((a) => ({
			key: a.kind,
			icon: a.icon,
			label: a.label,
			color: a.color,
			onPress: () => onSelect(a.kind),
		})),
		...(extraActions ?? []),
	];
	const toggle = () => {
		const next = !open;
		setOpen(next);
		Animated.timing(anim, { toValue: next ? 1 : 0, duration: 180, useNativeDriver: true }).start();
	};
	// Nothing the viewer can do — no button promising actions that aren't there.
	if (actions.length === 0) return null;
	const choose = (a: StackedAction) => {
		setOpen(false);
		anim.setValue(0);
		a.onPress();
	};
	return (
		<View pointerEvents="box-none" style={styles.wrap}>
			{open && <Pressable style={styles.backdrop} onPress={toggle} />}
			{actions.map((a, idx) => (
				<Animated.View
					key={a.key}
					pointerEvents={open ? 'auto' : 'none'}
					style={[
						styles.action,
						{
							transform: [{
								translateY: anim.interpolate({
									inputRange: [0, 1],
									outputRange: [0, -(idx + 1) * 68],
								}),
							}],
							opacity: anim,
						},
					]}
				>
					<Pressable style={styles.actionLabel} onPress={() => choose(a)} testID={`fab-action-label-${a.key}`}>
						<Text style={styles.actionLabelText}>{a.label}</Text>
					</Pressable>
					<Pressable
						style={[styles.actionButton, { backgroundColor: a.color }]}
						onPress={() => choose(a)}
						testID={`fab-action-${a.key}`}
					>
						<Ionicons name={a.icon} size={24} color="#fff" />
					</Pressable>
				</Animated.View>
			))}
			<Pressable style={styles.fab} onPress={toggle} testID="accounting-fab">
				<Animated.View style={{
					transform: [{ rotate: anim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '45deg'] }) }],
				}}>
					<Ionicons name="add" size={28} color="#fff" />
				</Animated.View>
			</Pressable>
		</View>
	);
};

const FAB_SIZE = 56;

const styles = StyleSheet.create({
	wrap: { position: 'absolute', right: 24, bottom: 24, width: FAB_SIZE },
	backdrop: { position: 'absolute', left: -9999, right: -9999, top: -9999, bottom: -9999, backgroundColor: 'rgba(0,0,0,0.1)' },
	fab: {
		width: FAB_SIZE, height: FAB_SIZE, borderRadius: FAB_SIZE / 2,
		backgroundColor: '#007AFF', alignItems: 'center', justifyContent: 'center',
		elevation: 6, shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 6, shadowOffset: { width: 0, height: 2 },
	},
	// Each action sits in the same column as the main FAB. The label floats to
	// the left of the circle so the circles line up in a perfect vertical stack.
	action: {
		position: 'absolute', right: 0, bottom: 0,
		width: FAB_SIZE, height: FAB_SIZE,
		flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end',
	},
	actionLabel: {
		position: 'absolute', right: FAB_SIZE + 12,
		backgroundColor: '#333', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 6,
		elevation: 2, shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 3, shadowOffset: { width: 0, height: 1 },
	},
	actionLabelText: { color: '#fff', fontSize: 13, fontWeight: '500' },
	actionButton: {
		width: FAB_SIZE, height: FAB_SIZE, borderRadius: FAB_SIZE / 2,
		alignItems: 'center', justifyContent: 'center',
		elevation: 4, shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 4, shadowOffset: { width: 0, height: 2 },
	},
});
